import { EnterElement, ScaleBand, Selection } from "d3"
import { SpectrogramTraceConfigJSON, getSpectrogramTraceId } from "../../../../Types/Trace"
import { D3OneToManyRenderable } from "../../../D3/D3OneToManyRenderable"
import { ModalityGraphGroupReactCallbacks } from "../../../../Types/ReactCallbacks"
import { D3SpectrogramTraceLegend } from "./D3SpectrogramTraceLegend"

export type D3SpectrogramGraphLegendConfig = {
	traces: SpectrogramTraceConfigJSON[]
	xOffset: number
	tracePositionScale: ScaleBand<string>
	graphId: string
}

export class D3SpectrogramGraphLegend extends D3OneToManyRenderable<SVGGElement, D3SpectrogramGraphLegendConfig, SpectrogramTraceConfigJSON, ModalityGraphGroupReactCallbacks> {
	private d3TraceLegends = new Map<string, D3SpectrogramTraceLegend>()

	constructor(root: SVGGElement, config: D3SpectrogramGraphLegendConfig, className: string, reactCallbacks: ModalityGraphGroupReactCallbacks) {
		super(root, config, className, reactCallbacks)
		this.mount()
	}

	protected updateDerivedState() {
		this.config.traces.forEach(trace =>
			this.d3TraceLegends.get(this.datumIdentifier(trace))?.updateConfig({
				trace,
				tracePositionScale: this.config.tracePositionScale,
				graphId: this.config.graphId,
				graphWidth: this.config.xOffset,
			})
		)
	}

	protected datumIdentifier(datum: SpectrogramTraceConfigJSON): string {
		return getSpectrogramTraceId(datum)
	}

	protected getConfigs(): SpectrogramTraceConfigJSON[] {
		return this.config.traces
	}

	protected enter(newElements: Selection<EnterElement, SpectrogramTraceConfigJSON, Element, any>): Selection<SVGGElement, SpectrogramTraceConfigJSON, Element, any> {
		const legend = newElements.append("g").attr("class", this.className)

		legend.each((config, index, nodes) => this.createChildren(config, index, nodes))

		return legend
	}

	protected update(updatedElements: Selection<SVGGElement, SpectrogramTraceConfigJSON, Element, any>): Selection<SVGGElement, SpectrogramTraceConfigJSON, Element, any> {
		this.config.traces.forEach(trace => this.d3TraceLegends.get(getSpectrogramTraceId(trace))?.render())
		return updatedElements
	}

	protected exit(exitedElements: Selection<SVGGElement, SpectrogramTraceConfigJSON, Element, any>): void {
		exitedElements.each(config => this.d3TraceLegends.delete(this.datumIdentifier(config)))
		exitedElements.remove()
	}

	protected createChildren(trace: SpectrogramTraceConfigJSON, index: number, nodes: ArrayLike<SVGGElement>): void {
		this.d3TraceLegends.set(
			getSpectrogramTraceId(trace),
			new D3SpectrogramTraceLegend(
				nodes[index],
				{ trace, tracePositionScale: this.config.tracePositionScale, graphId: this.config.graphId, graphWidth: this.config.xOffset },
				"d3-spectrogram-trace-legend",
				this.reactCallbacks
			)
		)
	}
}
